import React from 'react';
import { IUserSession } from '../../../@types/employee';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import { connect } from 'react-redux';
import BoreholeConstantRateTestHelper, { IBoreholeConstantRateTest } from '../../../@types/model/borehole/boreholeConstantRateTest';
import AppFunctionsService from '../../../services/appFunctionsService';
import PrimaryAccordionSummary from '../../customComponents/expansion/PrimaryAccordionSummary';
import SeparatedAccordion from '../../customComponents/expansion/SeparatedAccordion';
import { IBorehole } from '../../../@types/model/borehole/borehole';
import { Card, CircularProgress, Icon, IconButton } from '@material-ui/core';
import GeneralFunctions from '../../../store/general/functions';
import EditBoreholeConstantRate from '../dialog/constantRate/EditDialog';
import { Transitions } from '../../customComponents/animations/Transitions';
import BoreholeConstantRateEntryLogisticsView from './constantRateView/ConstantRateEntryLogisticsView';
import BoreholeConstantRateEntryLoggerView from './constantRateView/ConstantRateEntryLoggerView';
import BoreholeConstantRateEntryNeighbourLogView from './constantRateView/ConstantRateEntryNeighbourLogView';
import BoreholeConstantRateEntryLogView from './constantRateView/ConstantRateEntryLogView';
import BoreholeConstantRateRecoveryEntryLogView from './constantRateView/ConstantRateRecoveryEntryLogView';

interface IBoreholeConstantRatePDFViewProps {
    borehole : IBorehole;

    session : IUserSession;
}

interface IBoreholeConstantRatePDFViewState {
    boreholeConstantRateTests : Array<IBoreholeConstantRateTest>;
    isLoading : boolean;
}

class BoreholeConstantRatePDFViewComponent extends React.Component<IBoreholeConstantRatePDFViewProps, IBoreholeConstantRatePDFViewState> {
    private listener ?: () => void;
    constructor(props : IBoreholeConstantRatePDFViewProps) {
        super(props);
        this.state = {
            isLoading: false,
            boreholeConstantRateTests: [],
        };
    }

    public componentWillUnmount() : void {
        if (this.listener) this.listener();
    }

    public componentDidMount() : void {
        this.load();
    }

    public readonly load = () => {
        if (this.listener) this.listener();

        this.setState({
            boreholeConstantRateTests: [],
            isLoading: true,
        });

        this.listener = BoreholeConstantRateTestHelper.collection()
            .where('boreholeRef', '==', this.props.borehole.ref)
            .orderBy('date', 'desc')
            .onSnapshot((snapshot) => {
                if (!this.listener) return;
                this.setState({
                    boreholeConstantRateTests: snapshot.docs.map(x => x.data()).filter(x => !!x.documentName),
                    isLoading: false,
                });
            }, (error) => {
                GeneralFunctions.generalShowError(error, 'Error Loading ConstantRates');
            }, () => {
                this.listener = undefined;
            });
    };

    private readonly onViewClick = (event : React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();

        const constantRate = this.state.boreholeConstantRateTests.find(x => x.id === event.currentTarget.value);

        if (!constantRate?.documentURL) return;
        window.open(constantRate.documentURL, '_blank');
    };

    public readonly render = () => {
        const { boreholeConstantRateTests, isLoading } = this.state;
        const { borehole, session } = this.props;
        
        return (
            <div className={'fdc flx1 m10'}>
                <div className='fdc aife jcfe'>
                    <EditBoreholeConstantRate
                        transition={Transitions.Down}
                    />
                </div>
                {
                    isLoading &&
                    <div className='fdc flx1 aic jcc'>
                        <CircularProgress />
                    </div>
                }
                {
                    !isLoading &&
                    boreholeConstantRateTests.length === 0 &&
                    <div className='fdc flx1 aic jcc'>
                        <Typography className={'mb10'} color='inherit' variant='h5'>
                            No Test Data Exists!
                        </Typography>
                    </div>
                }
                {
                    !isLoading &&
                    !!boreholeConstantRateTests.length &&
                    <div className='fdc flx1 ais jcfs oya oxh mt12'>
                        {
                            boreholeConstantRateTests.map(boreholeConstantRateTest => (
                                <SeparatedAccordion key={boreholeConstantRateTest.id}>
                                    <PrimaryAccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <div className='fdr jcc aic flx1'>
                                            {
                                                AppFunctionsService.formatDateTimeToDateOnly(boreholeConstantRateTest.date) 
                                            }
                                            <span className='flx1' />
                                            <div className='mr15'>
                                                <IconButton
                                                    color='inherit'
                                                    value={boreholeConstantRateTest.id}
                                                    onClick={this.onViewClick}
                                                    size='small'
                                                >
                                                    <Icon>visibility</Icon>
                                                </IconButton>
                                            </div>
                                            {
                                                session.employee.IsBoreholeAdmin &&
                                                <div>
                                                    <EditBoreholeConstantRate
                                                        transition={Transitions.Down}
                                                        boreholeConstantRateTest={boreholeConstantRateTest}
                                                    />
                                                </div>
                                            }
                                        </div>
                                    </PrimaryAccordionSummary>
                                    <AccordionDetails className={'fdc flx1'}>
                                        <div className='fdc p5'>
                                            <Card className='fdr aic jcc p15 bcp'>
                                                <Typography className='flx1 aic jcc fs18 fwb cw'>
                                                    BOREHOLE LOGISTICS
                                                </Typography>
                                            </Card>
                                        </div>
                                        <div className='fdc aic jcc'>
                                            <BoreholeConstantRateEntryLogisticsView
                                                constantRateTest={boreholeConstantRateTest}
                                                borehole={borehole}
                                            />
                                        </div>
                                        {
                                            !!boreholeConstantRateTest.levelLoggerDepth &&
                                            <>
                                                <div className='fdc p5'>
                                                    <Card className='fdr aic jcc p15 bcp'>
                                                        <Typography className='flx1 aic jcc fs18 fwb cw'>
                                                            LEVEL LOGGER
                                                        </Typography>
                                                    </Card>
                                                </div>
                                                <div className='fdc aic jcc'>
                                                    <BoreholeConstantRateEntryLoggerView
                                                        constantRateTest={boreholeConstantRateTest}
                                                    />
                                                </div>
                                            </>
                                        }
                                        {
                                            !!boreholeConstantRateTest.neighbouringBoreholes.length &&
                                            <BoreholeConstantRateEntryNeighbourLogView
                                                constantRateTest={boreholeConstantRateTest}
                                            />

                                        }
                                        <BoreholeConstantRateEntryLogView
                                            constantRateTest={boreholeConstantRateTest}
                                        />
                                        {
                                            !!boreholeConstantRateTest.recoveryEntries[1].waterLevel &&
                                            <BoreholeConstantRateRecoveryEntryLogView
                                                constantRateTest={boreholeConstantRateTest}
                                            />

                                        }
                                    </AccordionDetails>
                                </SeparatedAccordion>
                            ))
                        }
                    </div>
                }
            </div>
        );
    };
}

const mapStateToProps = () => {
    return {};
};

const BoreholeConstantRatePDFView = connect(
    mapStateToProps,
)(BoreholeConstantRatePDFViewComponent);

export default BoreholeConstantRatePDFView;